import React, { useEffect, useState } from 'react';
import { ShoppingCart, Package } from 'lucide-react';
import { CheckoutForm } from '../CheckoutForm';
import { OrderConfirmation } from '../OrderConfirmation';
import { TimeSlotSelection } from '../TimeSlotSelection';
import { FulfillmentSelection } from '../FulfillmentSelection';
import { getEffectiveUnitPrice, getSalePercent } from '../utils/pricing';
import { useVocab, useIsAppointments } from '../hooks/useVocab';

/**
 * CartModal Component
 * Props:
 *  - visible: boolean controlling display
 *  - cart: array of cart items (product_id, name, price, quantity, images[])
 *  - checkoutStep: 'cart' | 'fulfillment' | 'timeslot' | 'checkout' | 'confirmation'
 *  - setCheckoutStep(step)
 *  - orderData: object | null (checkout result)
 *  - setOrderData(data)
 *  - storeSlug: string (for checkout form)
 *  - store: store object (settings drive fulfillment options / time slots)
 *  - onAddToCart(product)
 *  - onRemoveFromCart(productId)
 *  - setCart(newCartFn) for clearing cart
 *  - onClose() handles closing logic (parent manages state)
 */
export const CartModal = ({
    visible,
    cart,
    checkoutStep,
    setCheckoutStep,
    orderData,
    setOrderData,
    storeSlug,
    store,
    onAddToCart,
    onRemoveFromCart,
    setCart,
    onClose,
}) => {
    const t = useVocab();
    const isAppt = useIsAppointments();
    // Internal state to allow exit animation
    const [mounted, setMounted] = useState(visible);
    const [animatingOut, setAnimatingOut] = useState(false);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
    const [selectedFulfillment, setSelectedFulfillment] = useState(null);

    // Products that don't require delivery keep the legacy flow (no fulfillment step)
    const requiresDelivery = cart.some(item => item.requires_delivery !== false);

    useEffect(() => {
        if (visible) {
            setMounted(true);
            setAnimatingOut(false);
        } else if (mounted) {
            setAnimatingOut(true);
            const t = setTimeout(() => {
                setMounted(false);
            }, 260); // match transition duration
            return () => clearTimeout(t);
        }
    }, [visible, mounted]);

    if (!mounted) return null;

    const getItemTotal = (item) => {
        const base = getEffectiveUnitPrice(item) * item.quantity;
        const optionsTotal = (item.selectedPaidOptions || []).reduce((sum, opt) => sum + (parseFloat(opt.price) * item.quantity), 0);
        return base + optionsTotal;
    };

    const getTotalPrice = () => cart.reduce((sum, item) => sum + getItemTotal(item), 0);

    // Accessibility labels in Hebrew retained.
    return (
        <div
            className={[
                'fixed inset-0 flex items-center justify-center',
                'p-4 pb-20 sm:pb-4',
                'bg-black/60 backdrop-blur-sm transition-opacity duration-300',
                animatingOut ? 'opacity-0' : 'opacity-100',
            ].join(' ')}
            style={{ zIndex: 10000 }}
            role="dialog"
            aria-modal="true"
            aria-label="חלון סל קניות"
        >
            <div
                className={[
                    'relative w-full max-w-xl sm:max-w-2xl',
                    'max-h-[calc(100vh-8rem)] sm:max-h-[90vh] overflow-hidden',
                    'bg-white rounded-3xl shadow-2xl flex flex-col',
                    'transition-all duration-250 ease-out',
                    animatingOut
                        ? 'scale-95 opacity-0'
                        : 'scale-100 opacity-100',
                ].join(' ')}
            >
                {/* Sticky Header */}
                <div className="flex-none sticky top-0 z-20 bg-gradient-to-r from-success-500 to-success-500 px-5 py-4 flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3">
                        <ShoppingCart className="w-6 h-6 text-white" />
                        <h2 className="text-lg sm:text-xl font-bold text-white leading-tight">
                            {checkoutStep === 'cart' && t.cart}
                            {checkoutStep === 'fulfillment' && 'אופן קבלת ההזמנה'}
                            {checkoutStep === 'timeslot' && t.slotPickerTitle}
                            {checkoutStep === 'checkout' && t.checkoutTitle}
                            {checkoutStep === 'confirmation' && (isAppt ? 'אישור התור' : 'אישור הזמנה')}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 rounded-full p-2 transition-colors"
                        aria-label="סגור חלון"
                    >
                        ✕
                    </button>
                </div>

                {/* Scrollable Main Content (internal flex column) */}
                <div className="flex-1 flex flex-col min-h-0">{/* min-h-0 ensures child overflow works */}
                    {checkoutStep === 'fulfillment' ? (
                        // Fulfillment choice step — decides whether time-slot picking is needed.
                        // Carts that don't require delivery still offer on-premise options
                        // (takeaway / eat-in) when the store enabled them.
                        <FulfillmentSelection
                            storeSlug={storeSlug}
                            serviceOptions={store?.settings?.service_options}
                            timeSlotsEnabled={!!store?.settings?.time_slots_enabled}
                            onPremiseOnly={!requiresDelivery}
                            onComplete={(choice) => {
                                setSelectedFulfillment(choice);
                                if (choice.useTimeSlots) {
                                    setCheckoutStep('timeslot');
                                } else {
                                    setSelectedTimeSlot(null);
                                    setCheckoutStep('checkout');
                                }
                            }}
                        />
                    ) : checkoutStep === 'timeslot' ? (
                        // Time slot selection step - no extra wrapper, let component handle its own layout
                        <TimeSlotSelection
                            storeSlug={storeSlug}
                            onComplete={(timeSlotData) => {
                                setSelectedTimeSlot(timeSlotData);
                                setCheckoutStep('checkout');
                            }}
                            onSkip={() => {
                                    setSelectedTimeSlot(null);
                                    setCheckoutStep('checkout');
                                }}
                            />
                    ) : checkoutStep === 'checkout' ? (
                        // CheckoutForm manages its own scroll/layout
                        <CheckoutForm
                            storeSlug={storeSlug}
                            cart={cart}
                            modalLayout
                            preSelectedTimeSlot={selectedTimeSlot}
                            preSelectedFulfillment={selectedFulfillment}
                            onSuccess={(order) => {
                                setOrderData(order);
                                setCheckoutStep('confirmation');
                            }}
                            onCancel={() => setCheckoutStep('cart')}
                        />
                    ) : (
                        <div
                            className={[
                                'flex-1 overflow-y-auto overscroll-contain',
                                'scroll-py-4 px-5 py-4',
                                'touch-pan-y',
                            ].join(' ')}
                            aria-label={checkoutStep === 'cart' ? 'פרטי סל הקניות' : 'אישור הזמנה'}
                        >
                            {checkoutStep === 'cart' && (
                            <div className="space-y-3 pb-1">
                                {cart.length === 0 && (
                                    <div className="text-center text-mekomit-700 py-12">
                                        <p className="text-lg font-semibold">{isAppt ? 'עוד לא נבחרו תורים.' : 'הסל שלך ריק כרגע.'}</p>
                                        <p className="text-sm mt-2">{isAppt ? 'בחר שירות וקבע מועד כדי להמשיך.' : 'התאם פריטים והוסף אותם לסל כדי להמשיך.'}</p>
                                    </div>
                                )}
                                {cart.map((item, itemIdx) => {
                                    // Create unique key for each cart item based on product_id and selected options
                                    const optionsKey = (item.selectedPaidOptions || [])
                                        .map(opt => opt.name)
                                        .sort()
                                        .join('|');
                                    const uniqueKey = `${item.product_id}_${optionsKey}_${itemIdx}`;
                                    return (
                                        <div
                                            key={uniqueKey}
                                            className="flex sm:items-center items-start sm:flex-row flex-col gap-3 bg-mekomit-50 rounded-xl p-3 sm:p-3 border border-mekomit-100 shadow-sm"
                                        >
                                            {item.images && item.images.length > 0 ? (
                                                <img
                                                    src={item.images[0]}
                                                    alt={item.name}
                                                    className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div className="w-16 h-16 rounded-lg bg-mekomit-100 flex items-center justify-center flex-shrink-0">
                                                    <Package className="w-7 h-7 text-mekomit-300" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0 w-full">
                                                <h3 className="font-semibold text-mekomit-900 text-sm sm:text-base truncate" title={item.name}>{item.name}</h3>
                                                {getSalePercent(item) > 0 ? (
                                                    <p className="text-xs sm:text-sm mt-0.5">
                                                        <span className="text-danger-600 font-semibold">₪{getEffectiveUnitPrice(item).toFixed(2)}</span>
                                                        <span className="line-through text-mekomit-500 mr-1.5">₪{item.price}</span>
                                                        <span className="text-mekomit-700"> × {item.quantity}</span>
                                                        <span className="mr-1.5 text-[10px] sm:text-xs bg-danger-100 text-danger-700 px-1.5 py-0.5 rounded-full font-bold">-{getSalePercent(item)}%</span>
                                                    </p>
                                                ) : (
                                                    <p className="text-mekomit-700 text-xs sm:text-sm mt-0.5">₪{item.price} × {item.quantity}</p>
                                                )}
                                                {item.selectedPaidOptions && item.selectedPaidOptions.length > 0 && (
                                                    <div className="mt-1.5 bg-white rounded-md p-2 border border-mekomit-200">
                                                        <p className="text-[10px] sm:text-xs font-semibold text-mekomit-700 mb-1">תוספות נבחרות:</p>
                                                        {item.selectedPaidOptions.map((opt, idx) => (
                                                            <div key={idx} className="flex justify-between text-[10px] sm:text-xs text-mekomit-600">
                                                                <span>• {opt.name}</span>
                                                                <span>+₪{opt.price}</span>
                                                            </div>
                                                        ))}
                                                        <div className="flex justify-between text-[11px] sm:text-xs font-bold text-mekomit-800 mt-1 pt-1 border-t border-mekomit-200">
                                                            <span>סה"כ תוספות:</span>
                                                            <span>+₪{(item.selectedPaidOptions.reduce((sum, o) => sum + parseFloat(o.price), 0) * item.quantity).toFixed(2)}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex sm:flex-col flex-row sm:items-end items-center sm:ml-2 w-full sm:w-auto justify-between sm:justify-center gap-2 mt-1 sm:mt-0">
                                                <div className="flex items-center gap-2 order-1">
                                                    <button
                                                        onClick={() => onRemoveFromCart(item.product_id, item.selectedPaidOptions)}
                                                        className="bg-danger-500 hover:bg-danger-600 active:bg-danger-700 text-white w-7 h-7 rounded-md font-bold text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-500"
                                                        aria-label="הסר פריט"
                                                    >
                                                        -
                                                    </button>
                                                    <span className="text-mekomit-900 font-bold w-7 text-center text-sm select-none">
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        onClick={() => onAddToCart(item)}
                                                        className="bg-success-600 hover:bg-success-700 active:bg-success-700 text-white w-7 h-7 rounded-md font-bold text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success-500"
                                                        aria-label="הוסף פריט"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                                <div className="font-bold text-mekomit-900 text-xs sm:text-sm order-2 sm:order-none text-right sm:w-20">
                                                    ₪{getItemTotal(item).toFixed(2)}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {checkoutStep === 'confirmation' && (
                            <OrderConfirmation
                                orderData={orderData}
                                onClose={() => {
                                    onClose();
                                    setCart([]);
                                    setOrderData(null);
                                }}
                            />
                        )}
                        </div>
                    )}

                    {/* Sticky Footer (Cart step only) */}
                    {checkoutStep === 'cart' && (
                        <div className="flex-none sticky bottom-0 z-20 border-t border-mekomit-200 px-5 pt-3 pb-4 bg-mekomit-50/95 backdrop-blur supports-[backdrop-filter]:bg-mekomit-50 shadow-inner pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-sm sm:text-base font-bold text-mekomit-900">סה"כ:</span>
                                <span className="text-lg sm:text-xl font-bold text-mekomit-900">₪{getTotalPrice().toFixed(2)}</span>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2 text-sm">
                                <button
                                    onClick={() => setCheckoutStep('fulfillment')}
                                    className="flex-1 bg-gradient-to-r from-success-500 to-success-500 hover:from-success-600 hover:to-success-600 active:from-success-700 active:to-success-700 text-white font-bold py-2.5 sm:py-3 rounded-xl transition-all shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success-500 text-sm"
                                >
                                    {isAppt ? 'המשך לאישור התור' : 'המשך לסיום ההזמנה'}
                                </button>
                                <button
                                    onClick={() => {
                                        setCart([]);
                                        onClose();
                                        setCheckoutStep('cart');
                                    }}
                                    className="flex-1 bg-danger-100 hover:bg-danger-100 active:bg-danger-100 text-danger-700 font-bold py-2.5 sm:py-3 rounded-xl transition-all border border-danger-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-100 text-sm"
                                >
                                    {isAppt ? 'נקה הכל' : 'רוקן סל'}
                                </button>
                            </div>
                            <div className="mt-2 text-sm">
                                <button
                                    onClick={onClose}
                                    className="w-full bg-mekomit-100 hover:bg-mekomit-200 active:bg-mekomit-300 text-mekomit-900 font-bold py-2.5 rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mekomit-300"
                                >
                                    {isAppt ? 'חזרה לעיון בשירותים' : 'המשך בקניות'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CartModal;
